import { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft, Clock, MapPin, User, Wrench, AlertTriangle, Send,
  CheckCircle, XCircle, Play, Pause, Package, CalendarClock,
  MessageSquare, FileText, History, DollarSign, Star, Building2, Sparkles,
} from 'lucide-react'
import { DocumentPanel } from '@/components/DocumentPanel'
import { useAuthStore } from '@/store/auth'
import { apiGet, apiPost, apiPatch } from '@/lib/api'
import {
  cn, formatDateTime, timeAgo, isOverdue, formatCurrency,
  PRIORITY_LABELS, PRIORITY_COLORS, STATUS_LABELS, STATUS_COLORS,
} from '@/lib/utils'

// ── Types ───────────────────────────────────────────────────────────────────
interface WorkOrderDetail {
  id: string
  woNumber: string
  title: string
  description: string | null
  type: string
  priority: string
  status: string
  location: string | null
  faultCode: string | null
  slaDeadline: string | null
  slaBreached: boolean
  guestImpactScore: number
  pmsGuestName: string | null
  pmsGuestVipLevel: string | null
  pmsReservationId: string | null
  notes: string | null
  completionNotes: string | null
  labourCostGbp: number | null
  partsCostGbp: number | null
  contractorCostGbp: number | null
  totalCostGbp: number | null
  rating: number | null
  createdAt: string
  startedAt: string | null
  completedAt: string | null
  verifiedAt: string | null
  assignedAt: string | null
  durationMinutes: number | null
  asset: {
    id: string; name: string; code: string
    category: { name: string } | null
    building: { name: string } | null
    floor: { name: string } | null
    room: { name: string; number: string } | null
  } | null
  room: {
    id: string; name: string; number: string
    floor: { name: string; building: { name: string } } | null
  } | null
  assignedTo: { id: string; firstName: string; lastName: string; email: string; phone: string | null; avatarUrl: string | null } | null
  createdBy: { id: string; firstName: string; lastName: string } | null
  verifiedBy: { id: string; firstName: string; lastName: string } | null
  vendor: { id: string; name: string; phone: string | null; email: string | null } | null
  ppmSchedule: { id: string; title: string; frequency: string } | null
  comments: Comment[]
  attachments: Attachment[]
  statusHistory: StatusChange[]
  maintenanceCosts: MaintenanceCost[]
}

interface Comment {
  id: string
  body: string
  isInternal: boolean
  createdAt: string
  author: { id: string; firstName: string; lastName: string; avatarUrl: string | null }
}

interface Attachment {
  id: string
  fileName: string
  fileType: string
  fileSize: number
  url: string
  uploadedAt: string
}

interface StatusChange {
  id: string
  fromStatus: string | null
  toStatus: string
  reason: string | null
  changedAt: string
}

interface MaintenanceCost {
  id: string
  description: string
  amount: number
  currency: string
  type: string
}

// ── Status transition map ───────────────────────────────────────────────────
const VALID_TRANSITIONS: Record<string, string[]> = {
  NEW: ['ASSIGNED', 'IN_PROGRESS', 'CANCELLED'],
  ASSIGNED: ['IN_PROGRESS', 'ON_HOLD', 'CANCELLED'],
  IN_PROGRESS: ['ON_HOLD', 'PENDING_PARTS', 'PENDING_CONTRACTOR', 'COMPLETED'],
  ON_HOLD: ['IN_PROGRESS', 'CANCELLED'],
  PENDING_PARTS: ['IN_PROGRESS', 'CANCELLED'],
  PENDING_CONTRACTOR: ['IN_PROGRESS', 'CANCELLED'],
  COMPLETED: ['VERIFIED'],
  VERIFIED: [],
  CANCELLED: [],
}

const STATUS_ACTION_ICONS: Record<string, React.ElementType> = {
  IN_PROGRESS: Play,
  ON_HOLD: Pause,
  COMPLETED: CheckCircle,
  VERIFIED: Star,
  CANCELLED: XCircle,
  ASSIGNED: User,
  PENDING_PARTS: Package,
  PENDING_CONTRACTOR: Building2,
}

// ── Page Component ──────────────────────────────────────────────────────────
export function WorkOrderDetailPage() {
  const { woId } = useParams<{ woId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { currentPropertyId } = useAuthStore()
  const pid = currentPropertyId

  const [activeTab, setActiveTab] = useState<'comments' | 'history' | 'costs'>('comments')
  const [comment, setComment] = useState('')
  const [isInternal, setIsInternal] = useState(true)

  // Status transition state
  const [showStatusModal, setShowStatusModal] = useState(false)
  const [targetStatus, setTargetStatus] = useState('')
  const [statusReason, setStatusReason] = useState('')
  const [completionNotes, setCompletionNotes] = useState('')
  const [labourCost, setLabourCost] = useState('')
  const [partsCost, setPartsCost] = useState('')
  const [contractorCost, setContractorCost] = useState('')
  const [rating, setRating] = useState(0)

  // ── Queries ─────────────────────────────────────────────────────────────
  const { data: wo, isLoading, error } = useQuery({
    queryKey: ['work-order', woId],
    queryFn: () => apiGet<WorkOrderDetail>(`/properties/${pid}/work-orders/${woId}`),
    enabled: !!pid && !!woId,
  })

  // ── Mutations ───────────────────────────────────────────────────────────
  const statusMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiPatch(`/properties/${pid}/work-orders/${woId}/status`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-order', woId] })
      setShowStatusModal(false)
      resetStatusForm()
    },
  })

  const commentMutation = useMutation({
    mutationFn: (body: { body: string; isInternal: boolean }) =>
      apiPost(`/properties/${pid}/work-orders/${woId}/comments`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-order', woId] })
      setComment('')
    },
  })

  function resetStatusForm() {
    setTargetStatus('')
    setStatusReason('')
    setCompletionNotes('')
    setLabourCost('')
    setPartsCost('')
    setContractorCost('')
    setRating(0)
  }

  function handleStatusChange(status: string) {
    setTargetStatus(status)
    setShowStatusModal(true)
  }

  function submitStatusChange() {
    const body: Record<string, unknown> = { status: targetStatus }
    if (statusReason) body.reason = statusReason
    if (targetStatus === 'COMPLETED') {
      if (completionNotes) body.completionNotes = completionNotes
      if (labourCost) body.labourCostGbp = parseFloat(labourCost)
      if (partsCost) body.partsCostGbp = parseFloat(partsCost)
      if (contractorCost) body.contractorCostGbp = parseFloat(contractorCost)
      if (rating > 0) body.rating = rating
    }
    statusMutation.mutate(body)
  }

  function handleCommentSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!comment.trim()) return
    commentMutation.mutate({ body: comment.trim(), isInternal })
  }

  // ── Loading / Error states ──────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy-800" />
      </div>
    )
  }

  if (error || !wo) {
    return (
      <div className="text-center py-16">
        <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-4" />
        <h2 className="text-lg font-semibold text-navy-800">Work order not found</h2>
        <button onClick={() => navigate('/work-orders')} className="btn-secondary mt-4">
          Back to Work Orders
        </button>
      </div>
    )
  }

  const overdue = isOverdue(wo.slaDeadline) && !['COMPLETED', 'VERIFIED', 'CANCELLED'].includes(wo.status)
  const nextStatuses = VALID_TRANSITIONS[wo.status] ?? []
  const isClosed = ['COMPLETED', 'VERIFIED', 'CANCELLED'].includes(wo.status)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <button onClick={() => navigate('/work-orders')} className="mt-1 text-gray-400 hover:text-navy-800">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-gray-400 font-mono">{wo.woNumber}</span>
              <span className={PRIORITY_COLORS[wo.priority]}>{PRIORITY_LABELS[wo.priority]}</span>
              <span className={STATUS_COLORS[wo.status]}>{STATUS_LABELS[wo.status]}</span>
              {wo.slaBreached && (
                <span className="badge bg-red-100 text-red-800">SLA Breached</span>
              )}
              {overdue && !wo.slaBreached && (
                <span className="badge bg-orange-100 text-orange-800">Overdue</span>
              )}
              {wo.pmsGuestVipLevel && (
                <span className="badge bg-amber-100 text-amber-800">VIP Guest</span>
              )}
            </div>
            <h1 className="text-xl font-bold text-navy-800 mt-1">{wo.title}</h1>
            <p className="text-sm text-gray-400 mt-0.5">
              Created {timeAgo(wo.createdAt)} by {wo.createdBy?.firstName} {wo.createdBy?.lastName}
            </p>
          </div>
        </div>

        {/* Status action buttons */}
        {nextStatuses.length > 0 && (
          <div className="flex gap-2 flex-shrink-0 flex-wrap">
            {nextStatuses.map((s) => {
              const Icon = STATUS_ACTION_ICONS[s] ?? Play
              const isDestructive = s === 'CANCELLED'
              return (
                <button
                  key={s}
                  onClick={() => handleStatusChange(s)}
                  className={isDestructive ? 'btn-danger' : 'btn-secondary'}
                >
                  <Icon className="w-4 h-4" />
                  {STATUS_LABELS[s]}
                </button>
              )
            })}
          </div>
        )}
      </div>

      {/* Main grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column — details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Safety warning */}
          {wo.notes && wo.notes.includes('SAFETY WARNING') && (
            <div className="card border-red-300 bg-red-50/60">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h2 className="text-sm font-semibold text-red-800 mb-1">Safety Warning</h2>
                  <p className="text-sm text-red-700">
                    {wo.notes.split('\n').find(l => l.includes('SAFETY WARNING'))?.replace(/^⚠ SAFETY WARNING:\s*/, '')}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Description */}
          {wo.description && (
            <div className="card">
              <h2 className="text-sm font-semibold text-navy-800 mb-2">Description</h2>
              <p className="text-sm text-gray-600 whitespace-pre-wrap">{wo.description}</p>
            </div>
          )}

          {/* AI triage notes */}
          {wo.notes && wo.notes.includes('AI Triage') && (
            <div className="card bg-purple-50/30 border-purple-200">
              <h2 className="text-sm font-semibold text-navy-800 mb-2 flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-purple-500" /> AI Classification
              </h2>
              <p className="text-sm text-gray-600">
                {wo.notes.split('\n').find(l => l.includes('AI Triage'))}
              </p>
            </div>
          )}

          {/* Completion info */}
          {wo.completionNotes && (
            <div className="card border-green-200 bg-green-50/50">
              <h2 className="text-sm font-semibold text-green-800 mb-2 flex items-center gap-2">
                <CheckCircle className="w-4 h-4" /> Completion Notes
              </h2>
              <p className="text-sm text-green-700 whitespace-pre-wrap">{wo.completionNotes}</p>
              {wo.rating && (
                <div className="flex items-center gap-1 mt-3">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Star
                      key={i}
                      className={cn('w-4 h-4', i <= wo.rating! ? 'text-amber-500 fill-amber-500' : 'text-gray-300')}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Tabs */}
          <div className="card p-0">
            <div className="flex border-b border-gray-100">
              {([
                { key: 'comments', label: 'Comments', icon: MessageSquare, count: wo.comments.length },
                { key: 'history', label: 'Status History', icon: History, count: wo.statusHistory.length },
                { key: 'costs', label: 'Costs', icon: DollarSign, count: wo.maintenanceCosts.length },
              ] as const).map(({ key, label, icon: Icon, count }) => (
                <button
                  key={key}
                  onClick={() => setActiveTab(key)}
                  className={cn(
                    'flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors',
                    activeTab === key
                      ? 'border-navy-800 text-navy-800'
                      : 'border-transparent text-gray-400 hover:text-gray-600'
                  )}
                >
                  <Icon className="w-4 h-4" />
                  {label}
                  {count > 0 && (
                    <span className="bg-gray-100 text-gray-600 rounded-full px-1.5 py-0.5 text-xs">{count}</span>
                  )}
                </button>
              ))}
            </div>

            <div className="p-6">
              {/* Comments tab */}
              {activeTab === 'comments' && (
                <div className="space-y-4">
                  {wo.comments.length === 0 && (
                    <p className="text-sm text-gray-400 text-center py-4">No comments yet</p>
                  )}
                  {wo.comments.map((c) => (
                    <div key={c.id} className="flex gap-3">
                      <div className="w-8 h-8 rounded-full bg-navy-100 flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-semibold text-navy-800">
                          {c.author.firstName[0]}{c.author.lastName[0]}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-navy-800">
                            {c.author.firstName} {c.author.lastName}
                          </span>
                          {c.isInternal && (
                            <span className="text-xs text-gray-400 bg-gray-100 rounded px-1.5 py-0.5">Internal</span>
                          )}
                          <span className="text-xs text-gray-400">{timeAgo(c.createdAt)}</span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1 whitespace-pre-wrap">{c.body}</p>
                      </div>
                    </div>
                  ))}

                  {/* Add comment form */}
                  {!isClosed && (
                    <form onSubmit={handleCommentSubmit} className="border-t border-gray-100 pt-4 mt-4">
                      <textarea
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        placeholder="Add a comment..."
                        rows={3}
                        className="input resize-none"
                      />
                      <div className="flex items-center justify-between mt-2">
                        <label className="flex items-center gap-2 text-sm text-gray-500 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={isInternal}
                            onChange={(e) => setIsInternal(e.target.checked)}
                            className="rounded border-gray-300 text-navy-800 focus:ring-navy-500"
                          />
                          Internal note
                        </label>
                        <button
                          type="submit"
                          disabled={!comment.trim() || commentMutation.isPending}
                          className="btn-primary"
                        >
                          <Send className="w-4 h-4" />
                          {commentMutation.isPending ? 'Sending...' : 'Comment'}
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              )}

              {/* History tab */}
              {activeTab === 'history' && (
                <div className="relative">
                  {wo.statusHistory.length === 0 && (
                    <p className="text-sm text-gray-400 text-center py-4">No status changes</p>
                  )}
                  <div className="space-y-0">
                    {wo.statusHistory.map((h, i) => (
                      <div key={h.id} className="flex gap-3 pb-4">
                        <div className="flex flex-col items-center">
                          <div className={cn(
                            'w-3 h-3 rounded-full border-2 flex-shrink-0',
                            i === wo.statusHistory.length - 1
                              ? 'bg-navy-800 border-navy-800'
                              : 'bg-white border-gray-300'
                          )} />
                          {i < wo.statusHistory.length - 1 && (
                            <div className="w-0.5 flex-1 bg-gray-200 mt-1" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0 -mt-0.5">
                          <div className="flex items-center gap-2 flex-wrap">
                            {h.fromStatus && (
                              <>
                                <span className={STATUS_COLORS[h.fromStatus]}>{STATUS_LABELS[h.fromStatus]}</span>
                                <span className="text-gray-400 text-xs">&rarr;</span>
                              </>
                            )}
                            <span className={STATUS_COLORS[h.toStatus]}>{STATUS_LABELS[h.toStatus]}</span>
                          </div>
                          <p className="text-xs text-gray-400 mt-1">{formatDateTime(h.changedAt)}</p>
                          {h.reason && (
                            <p className="text-sm text-gray-500 mt-1">{h.reason}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Costs tab */}
              {activeTab === 'costs' && (
                <div className="space-y-4">
                  {wo.totalCostGbp != null && wo.totalCostGbp > 0 ? (
                    <>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                        <div>
                          <p className="text-xs text-gray-400">Labour</p>
                          <p className="text-lg font-semibold text-navy-800">{formatCurrency(wo.labourCostGbp)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-400">Parts</p>
                          <p className="text-lg font-semibold text-navy-800">{formatCurrency(wo.partsCostGbp)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-400">Contractor</p>
                          <p className="text-lg font-semibold text-navy-800">{formatCurrency(wo.contractorCostGbp)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-400">Total</p>
                          <p className="text-lg font-bold text-navy-800">{formatCurrency(wo.totalCostGbp)}</p>
                        </div>
                      </div>
                      {wo.maintenanceCosts.length > 0 && (
                        <div className="border-t border-gray-100 pt-4">
                          <h3 className="text-sm font-medium text-navy-800 mb-2">Cost Breakdown</h3>
                          {wo.maintenanceCosts.map((c) => (
                            <div key={c.id} className="flex justify-between py-1.5 text-sm">
                              <span className="text-gray-600">{c.description}</span>
                              <span className="font-medium text-navy-800">{formatCurrency(c.amount, c.currency)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <p className="text-sm text-gray-400 text-center py-4">No costs recorded</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right column — sidebar info */}
        <div className="space-y-6">
          {/* SLA & Timing */}
          <div className="card">
            <h2 className="text-sm font-semibold text-navy-800 mb-3 flex items-center gap-2">
              <Clock className="w-4 h-4 text-gray-400" /> Timing
            </h2>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Created</span>
                <span className="text-navy-800">{formatDateTime(wo.createdAt)}</span>
              </div>
              {wo.slaDeadline && (
                <div className="flex justify-between">
                  <span className="text-gray-500">SLA Deadline</span>
                  <span className={cn('font-medium', overdue ? 'text-red-600' : 'text-navy-800')}>
                    {formatDateTime(wo.slaDeadline)}
                  </span>
                </div>
              )}
              {wo.assignedAt && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Assigned</span>
                  <span className="text-navy-800">{formatDateTime(wo.assignedAt)}</span>
                </div>
              )}
              {wo.startedAt && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Started</span>
                  <span className="text-navy-800">{formatDateTime(wo.startedAt)}</span>
                </div>
              )}
              {wo.completedAt && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Completed</span>
                  <span className="text-navy-800">{formatDateTime(wo.completedAt)}</span>
                </div>
              )}
              {wo.durationMinutes != null && (
                <div className="flex justify-between border-t border-gray-100 pt-3">
                  <span className="text-gray-500">Duration</span>
                  <span className="font-medium text-navy-800">
                    {wo.durationMinutes >= 60
                      ? `${Math.floor(wo.durationMinutes / 60)}h ${wo.durationMinutes % 60}m`
                      : `${wo.durationMinutes}m`}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Assigned To */}
          <div className="card">
            <h2 className="text-sm font-semibold text-navy-800 mb-3 flex items-center gap-2">
              <User className="w-4 h-4 text-gray-400" /> Assigned To
            </h2>
            {wo.assignedTo ? (
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-navy-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-semibold text-navy-800">
                    {wo.assignedTo.firstName[0]}{wo.assignedTo.lastName[0]}
                  </span>
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-navy-800">{wo.assignedTo.firstName} {wo.assignedTo.lastName}</p>
                  <p className="text-xs text-gray-400 truncate">{wo.assignedTo.email}</p>
                  {wo.assignedTo.phone && <p className="text-xs text-gray-400">{wo.assignedTo.phone}</p>}
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-400">Unassigned</p>
            )}
          </div>

          {/* Asset */}
          {wo.asset && (
            <div className="card">
              <h2 className="text-sm font-semibold text-navy-800 mb-3 flex items-center gap-2">
                <Wrench className="w-4 h-4 text-gray-400" /> Asset
              </h2>
              <Link to={`/assets/${wo.asset.id}`} className="block hover:bg-gray-50 -m-2 p-2 rounded-lg transition-colors">
                <p className="text-sm font-medium text-navy-800">{wo.asset.name}</p>
                <p className="text-xs text-gray-400 font-mono">{wo.asset.code}</p>
                {wo.asset.category && <p className="text-xs text-gray-400 mt-1">{wo.asset.category.name}</p>}
                {wo.asset.building && (
                  <p className="text-xs text-gray-400">
                    {wo.asset.building.name}
                    {wo.asset.floor && ` \u2192 ${wo.asset.floor.name}`}
                    {wo.asset.room && ` \u2192 ${wo.asset.room.number}`}
                  </p>
                )}
              </Link>
            </div>
          )}

          {/* Location */}
          {(wo.room || wo.location) && (
            <div className="card">
              <h2 className="text-sm font-semibold text-navy-800 mb-3 flex items-center gap-2">
                <MapPin className="w-4 h-4 text-gray-400" /> Location
              </h2>
              {wo.room && (
                <div>
                  <p className="text-sm font-medium text-navy-800">Room {wo.room.number}</p>
                  <p className="text-xs text-gray-400">{wo.room.name}</p>
                  {wo.room.floor && (
                    <p className="text-xs text-gray-400">
                      {wo.room.floor.building.name} \u2192 {wo.room.floor.name}
                    </p>
                  )}
                </div>
              )}
              {wo.location && (
                <p className="text-sm text-gray-600 mt-1">{wo.location}</p>
              )}
            </div>
          )}

          {/* Vendor */}
          {wo.vendor && (
            <div className="card">
              <h2 className="text-sm font-semibold text-navy-800 mb-3 flex items-center gap-2">
                <Building2 className="w-4 h-4 text-gray-400" /> Contractor
              </h2>
              <p className="text-sm font-medium text-navy-800">{wo.vendor.name}</p>
              {wo.vendor.email && <p className="text-xs text-gray-400">{wo.vendor.email}</p>}
              {wo.vendor.phone && <p className="text-xs text-gray-400">{wo.vendor.phone}</p>}
            </div>
          )}

          {/* Guest Info */}
          {wo.pmsGuestName && (
            <div className="card border-amber-200 bg-amber-50/30">
              <h2 className="text-sm font-semibold text-navy-800 mb-3 flex items-center gap-2">
                <Star className="w-4 h-4 text-amber-500" /> Guest Info
              </h2>
              <p className="text-sm font-medium text-navy-800">{wo.pmsGuestName}</p>
              {wo.pmsGuestVipLevel && (
                <span className="badge bg-amber-100 text-amber-800 mt-1">VIP: {wo.pmsGuestVipLevel}</span>
              )}
              {wo.pmsReservationId && (
                <p className="text-xs text-gray-400 mt-1">Reservation: {wo.pmsReservationId}</p>
              )}
              <p className="text-xs text-gray-400 mt-1">Impact Score: {wo.guestImpactScore}/10</p>
            </div>
          )}

          {/* PPM Link */}
          {wo.ppmSchedule && (
            <div className="card">
              <h2 className="text-sm font-semibold text-navy-800 mb-3 flex items-center gap-2">
                <CalendarClock className="w-4 h-4 text-gray-400" /> PPM Schedule
              </h2>
              <p className="text-sm font-medium text-navy-800">{wo.ppmSchedule.title}</p>
              <p className="text-xs text-gray-400">Frequency: {wo.ppmSchedule.frequency}</p>
            </div>
          )}

          {/* Meta */}
          <div className="card">
            <h2 className="text-sm font-semibold text-navy-800 mb-3 flex items-center gap-2">
              <FileText className="w-4 h-4 text-gray-400" /> Details
            </h2>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Type</span>
                <span className="text-navy-800">{wo.type}</span>
              </div>
              {wo.faultCode && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Fault Code</span>
                  <span className="text-navy-800 font-mono">{wo.faultCode}</span>
                </div>
              )}
              {wo.verifiedBy && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Verified By</span>
                  <span className="text-navy-800">{wo.verifiedBy.firstName} {wo.verifiedBy.lastName}</span>
                </div>
              )}
            </div>
          </div>

          {/* Documents from DocFlow */}
          <DocumentPanel
            entityType="workorder"
            entityId={wo.id}
            title="Attachments"
            compact
          />
        </div>
      </div>

      {/* Status Change Modal */}
      {showStatusModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
            <h2 className="text-lg font-semibold text-navy-800 mb-4">
              Change Status to {STATUS_LABELS[targetStatus]}
            </h2>

            <div className="space-y-4">
              <div>
                <label className="label">Reason / Notes</label>
                <textarea
                  value={statusReason}
                  onChange={(e) => setStatusReason(e.target.value)}
                  rows={2}
                  className="input resize-none"
                  placeholder="Optional reason for this change..."
                />
              </div>

              {targetStatus === 'COMPLETED' && (
                <>
                  <div>
                    <label className="label">Completion Notes</label>
                    <textarea
                      value={completionNotes}
                      onChange={(e) => setCompletionNotes(e.target.value)}
                      rows={3}
                      className="input resize-none"
                      placeholder="What was done to resolve this..."
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="label">Labour</label>
                      <input
                        type="number" step="0.01" min="0"
                        value={labourCost} onChange={(e) => setLabourCost(e.target.value)}
                        className="input" placeholder="0.00"
                      />
                    </div>
                    <div>
                      <label className="label">Parts</label>
                      <input
                        type="number" step="0.01" min="0"
                        value={partsCost} onChange={(e) => setPartsCost(e.target.value)}
                        className="input" placeholder="0.00"
                      />
                    </div>
                    <div>
                      <label className="label">Contractor</label>
                      <input
                        type="number" step="0.01" min="0"
                        value={contractorCost} onChange={(e) => setContractorCost(e.target.value)}
                        className="input" placeholder="0.00"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="label">Rating</label>
                    <div className="flex gap-1">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => setRating(i === rating ? 0 : i)}
                          className="p-0.5"
                        >
                          <Star className={cn('w-6 h-6', i <= rating ? 'text-amber-500 fill-amber-500' : 'text-gray-300')} />
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => { setShowStatusModal(false); resetStatusForm() }}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={submitStatusChange}
                disabled={statusMutation.isPending}
                className={targetStatus === 'CANCELLED' ? 'btn-danger' : 'btn-primary'}
              >
                {statusMutation.isPending ? 'Saving...' : `Set ${STATUS_LABELS[targetStatus]}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
